import { API_URL, formatApiError } from '../utils/api';
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '../components/ui/tabs';
import { DataTable } from '../components/DataTable';
import { DataToolbar } from '../components/DataToolbar';
import { DataPagination } from '../components/DataPagination';
import {
  UserCircleGear,
  Plus,
  MagnifyingGlass,
  Trash,
  GraduationCap,
  User,
  CurrencyDollar,
  Gear,
  PencilSimple,
  Eye,
  ChartLineUp,
  Spinner,
  ArrowUp,
  ArrowDown,
  ArrowsClockwise,
  CaretDown,
  CaretUp,
  DeviceMobile,
  ShieldCheck,
  Prohibit,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const YES_NO = [
  { value: 'Yes', label: 'Yes' },
  { value: 'No', label: 'No' },
];

function timeAgo(iso) {
  try {
    const ts = new Date(iso).getTime();
    if (!ts) return '';
    const seconds = Math.floor((Date.now() - ts) / 1000);
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  } catch (e) { return ''; }
}

const initialFormData = {
  user_id: '',
  cashier_type: 'Agent',
  campus_id: '',
  assigned_float: '',
  assigned_cards: '',
  sell_bottle: 'No',
  recharge: 'Yes',
  recharge_limit: '',
  offer: 'No',
  sub_free_plan: 'No',
  send_sms: 'No',
};

const formSteps = [
  { label: 'Assignment', icon: User },
  { label: 'Float & Cards', icon: CurrencyDollar },
  { label: 'Settings', icon: Gear },
];

export default function CashiersPage() {
  const { isSuperAdmin } = useAuth();
  const { entities, selectedEntity } = useTenant();
  const [cashiers, setCashiers] = useState([]);
  const [users, setUsers] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterEntity, setFilterEntity] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ ...initialFormData });
  const [step, setStep] = useState(0);

  // Edit-cashier state
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editForm, setEditForm] = useState({
    cashier_type: 'Agent',
    campus_id: '',
    recharge_limit: '',
    sell_bottle: 'N',
    recharge: 'Y',
    special_offer: 'N',
    sub_free_plan: 'N',
    send_sms: 'Y',
    status: 'Active',
    float_delta: '',
    card_delta: '',
  });
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editShowMore, setEditShowMore] = useState(false);
  const [editTab, setEditTab] = useState('float');
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  // Mobile session admin (super-admin only)
  const [mobileSession, setMobileSession] = useState(null);
  const [mobileSessionLoading, setMobileSessionLoading] = useState(false);
  const [revokingSession, setRevokingSession] = useState(false);

  const authHeaders = () => {
    const token = localStorage.getItem('access_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchCashiers = useCallback(async () => {
    try {
      let url = `${API_URL}/api/cashiers`;
      if (filterEntity && filterEntity !== 'all') url += `?entity_id=${filterEntity}`;
      const { data } = await axios.get(url, { withCredentials: true, headers: authHeaders() });
      setCashiers(data);
    } catch (error) {
      toast.error('Failed to fetch cashiers');
    } finally {
      setLoading(false);
    }
  }, [filterEntity]);

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/users`, { withCredentials: true, headers: authHeaders() });
      setUsers(data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, []);

  const fetchCampuses = useCallback(async (entityId) => {
    try {
      let url = `${API_URL}/api/campuses`;
      if (entityId && entityId !== 'all') url += `?entity_id=${entityId}`;
      const { data } = await axios.get(url, { withCredentials: true, headers: authHeaders() });
      setCampuses(data);
    } catch (error) {
      console.error('Error fetching campuses:', error);
    }
  }, []);

  useEffect(() => {
    // On mount: pull fresh balances from middleware first, then load local cashiers
    (async () => {
      try {
        await axios.post(`${API_URL}/api/cashiers/sync-balances`, {}, { headers: authHeaders() });
      } catch (e) { /* non-blocking */ }
      fetchCashiers();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { fetchUsers(); }, [fetchUsers]);
  useEffect(() => { fetchCampuses(filterEntity); }, [filterEntity, fetchCampuses]);

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData({ ...initialFormData });
    setStep(0);
  };

  const openCreateDialog = () => {
    resetForm();
    const eid = selectedEntity?.id || '';
    if (eid) fetchCampuses(eid);
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/cashiers`, formData, {
        withCredentials: true, headers: authHeaders()
      });
      try {
        await axios.post(`${API_URL}/api/cashiers/sync-balances`, {}, { headers: authHeaders() });
      } catch (e) { /* non-blocking */ }
      toast.success('Cashier created successfully');
      setIsDialogOpen(false);
      resetForm();
      fetchCashiers();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to create cashier'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (cashier) => {
    const name = cashier.user_fullname || cashier.user_name || 'this cashier';
    if (!window.confirm(`Remove ${name} as cashier?`)) return;
    try {
      await axios.delete(`${API_URL}/api/cashiers/${cashier.id}`, {
        withCredentials: true, headers: authHeaders()
      });
      toast.success('Cashier removed');
      fetchCashiers();
    } catch (error) {
      toast.error('Failed to remove cashier');
    }
  };

  const yn = (v, fallback = 'N') => {
    if (v == null) return fallback;
    const s = String(v).trim().toUpperCase();
    if (['Y', 'YES', 'TRUE', '1'].includes(s)) return 'Y';
    if (['N', 'NO', 'FALSE', '0'].includes(s)) return 'N';
    return fallback;
  };

  // Map any legacy `cashier_type` value to the canonical UI value.
  const normalizeCashierType = (t) => {
    const s = String(t || '').toLowerCase();
    if (s.includes('ambassador')) return 'Ambassador';
    return 'Agent';
  };

  const openEditDialog = async (cashier) => {
    // Open the dialog immediately with a loader, then fetch fresh balances.
    setEditing(cashier);
    setEditForm({
      cashier_type: normalizeCashierType(cashier.cashier_type),
      campus_id: cashier.campus_id || '',
      recharge_limit: cashier.recharge_limit || '',
      sell_bottle: yn(cashier.sell_bottle, 'N'),
      recharge: yn(cashier.recharge, 'Y'),
      special_offer: yn(cashier.offer, 'N'),
      sub_free_plan: yn(cashier.sub_free_plan, 'N'),
      send_sms: yn(cashier.send_sms, 'Y'),
      status: cashier.status === 'active' ? 'Active' : 'Inactive',
      float_delta: '',
      card_delta: '',
    });
    setEditTab('float');
    setAnalytics(null);
    setEditLoading(true);
    setEditOpen(true);

    // Fetch fresh balances from middleware in the background
    try {
      await axios.post(`${API_URL}/api/cashiers/sync-balances`, {}, { headers: authHeaders() });
      const { data } = await axios.get(`${API_URL}/api/cashiers`, { headers: authHeaders() });
      const fresh = (data || []).find(c => c.id === cashier.id);
      if (fresh) setEditing(fresh);
      setCashiers(data || []);
    } catch (e) { /* non-blocking; fall back to row data */ }
    finally { setEditLoading(false); }
  };

  // Lazy-load analytics when the Analytics tab is opened for the current cashier
  useEffect(() => {
    if (!editOpen || !editing || editTab !== 'analytics' || analytics) return;
    let cancelled = false;
    (async () => {
      setAnalyticsLoading(true);
      try {
        const { data } = await axios.get(`${API_URL}/api/cashiers/${editing.id}/analytics`, {
          headers: authHeaders(),
        });
        if (!cancelled) setAnalytics(data);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e, 'Failed to load analytics'));
      } finally {
        if (!cancelled) setAnalyticsLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editOpen, editTab, editing?.id]);

  // Lazy-load mobile session info when the Mobile Session tab is opened
  useEffect(() => {
    if (!editOpen || !editing || editTab !== 'mobile-session' || !isSuperAdmin) return;
    let cancelled = false;
    (async () => {
      setMobileSessionLoading(true);
      try {
        const { data } = await axios.get(
          `${API_URL}/api/admin/cashiers/${editing.id}/mobile-session`,
          { headers: authHeaders() },
        );
        if (!cancelled) setMobileSession(data);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e, 'Failed to load mobile session'));
      } finally {
        if (!cancelled) setMobileSessionLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editOpen, editTab, editing?.id]);

  const handleRevokeMobileSession = async () => {
    if (!editing) return;
    if (!window.confirm(
      `Force-revoke ${editing.user_name || 'this cashier'}'s mobile session?\n\n` +
      `Their phone will be signed out immediately. They must re-login with phone + password (PIN alone won't work).`
    )) return;
    setRevokingSession(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/cashiers/${editing.id}/mobile-session/revoke`,
        {},
        { headers: authHeaders() },
      );
      toast.success('Mobile session revoked');
      // Refresh the view
      const { data } = await axios.get(
        `${API_URL}/api/admin/cashiers/${editing.id}/mobile-session`,
        { headers: authHeaders() },
      );
      setMobileSession(data);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to revoke session'));
    } finally {
      setRevokingSession(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editing) return;
    setEditSubmitting(true);
    try {
      const payload = {
        cashier_type: editForm.cashier_type,
        campus_id: editForm.campus_id,
        recharge_limit: editForm.recharge_limit ? Number(editForm.recharge_limit) : 0,
        sell_bottle: editForm.sell_bottle,
        recharge: editForm.recharge,
        special_offer: editForm.special_offer,
        sub_free_plan: editForm.sub_free_plan,
        send_sms: editForm.send_sms,
        status: editForm.status,
        float_delta: editForm.float_delta ? Number(editForm.float_delta) : 0,
        card_delta: editForm.card_delta ? Number(editForm.card_delta) : 0,
      };
      await axios.put(`${API_URL}/api/cashiers/${editing.id}`, payload, {
        withCredentials: true, headers: authHeaders(),
      });
      // Pull fresh balances from the middleware so the table reflects the new float/card state
      try {
        await axios.post(`${API_URL}/api/cashiers/sync-balances`, {}, { headers: authHeaders() });
      } catch (e) { /* non-blocking */ }
      toast.success('Cashier updated');
      setEditOpen(false);
      setEditing(null);
      fetchCashiers();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to update cashier'));
    } finally {
      setEditSubmitting(false);
    }
  };

  const filteredCashiers = cashiers.filter(c => {
    const name = (c.user_fullname || c.user_name || '').toLowerCase();
    return name.includes(searchQuery.toLowerCase()) ||
      (c.campus_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (c.store_name || '').toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Pagination (client-side over filtered list)
  const totalFiltered = filteredCashiers.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedCashiers = filteredCashiers.slice(
    (safePage - 1) * pageSize,
    safePage * pageSize
  );

  // Reset to page 1 whenever filters change
  useEffect(() => { setCurrentPage(1); }, [searchQuery, filterEntity]);

  // Filter users who aren't already cashiers
  const existingCashierUserIds = cashiers.map(c => c.user_id);
  const availableUsers = users.filter(u => !existingCashierUserIds.includes(u.id));

  return (
    <div className="space-y-3 pb-6" data-testid="cashiers-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-[#1C1917] tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          Cashiers
        </h1>
        <div className="flex items-center gap-2">
          <Button
            onClick={openCreateDialog}
            className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg"
            data-testid="create-cashier-btn"
          >
            <Plus size={18} className="mr-2" />
            Add Cashier
          </Button>
        </div>
      </div>

      {/* Toolbar */}
      <DataToolbar
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder="Search by name, campus, store…"
        testId="cashiers-toolbar"
        onClearAll={() => { setSearchQuery(''); setFilterEntity('all'); }}
        filters={isSuperAdmin ? [{
          key: 'entity',
          label: 'Entity',
          value: filterEntity,
          onChange: setFilterEntity,
          options: [{ value: 'all', label: 'All entities' }, ...entities.map((e) => ({ value: e.id, label: e.entity_name }))],
        }] : []}
      />

      <DataTable
        columns={[
          { key: 'cashier', label: 'Cashier', width: 'minmax(220px,2fr)', render: (c) => (
            <span className="flex items-center gap-3 min-w-0">
              <span className="shrink-0 w-8 h-8 rounded-full bg-[#0A2540]/10 group-hover:bg-[#EDE6D8] flex items-center justify-center">
                <span className="text-[10px] font-bold text-[#0A2540] group-hover:text-[#1C1917]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  {(c.user_fullname || c.user_name || '?').charAt(0).toUpperCase()}
                </span>
              </span>
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold text-[#1C1917] group-hover:text-[#1C1917]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  {c.user_fullname || c.user_name}
                </span>
              </span>
            </span>
          ) },
          { key: 'cashier_type', label: 'Type',   width: 'minmax(140px,1.1fr)', tone: 'muted', render: (c) => normalizeCashierType(c.cashier_type) },
          { key: 'campus_name',  label: 'Campus', width: 'minmax(160px,1.3fr)', tone: 'muted' },
          { key: 'assigned_float', label: 'Float', width: 'minmax(120px,1fr)', mono: true, align: 'right', tone: 'strong',
            render: (c) => Number(c.assigned_float || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) },
          { key: 'assigned_cards', label: 'Cards', width: 'minmax(80px,0.7fr)', mono: true, align: 'right', tone: 'strong',
            render: (c) => Number(c.assigned_cards || 0).toLocaleString() },
          { key: 'status', label: 'Status', width: 'minmax(100px,0.9fr)', render: (c) => (
            <span className="inline-flex items-center gap-1.5">
              <span className={`w-1.5 h-1.5 rounded-full ${c.status === 'active' ? 'bg-emerald-500' : 'bg-rose-500'} group-hover:bg-[#FFFFFF]`} />
              <span className={`text-xs font-medium ${c.status === 'active' ? 'text-emerald-700' : 'text-rose-700'} group-hover:text-[#1C1917]`} style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                {c.status || '—'}
              </span>
            </span>
          ) },
        ]}
        rows={paginatedCashiers}
        loading={loading}
        rowKey={(c) => c.id}
        onRowClick={(c) => openEditDialog(c)}
        emptyIcon={<User size={20} className="text-slate-500" />}
        emptyTitle="No cashiers yet"
        emptyText={searchQuery ? 'Try clearing your search.' : 'Add a cashier to get started.'}
        testIdPrefix="cashier"
        actionRender={(c) => (
          <span className="flex items-center justify-end gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => { e.stopPropagation(); openEditDialog(c); }}
              className="text-[#0A2540] hover:text-[#0A2540] hover:bg-[#0A2540]/10 group-hover:text-[#1C1917] group-hover:bg-[#EDE6D8] h-8 w-8 p-0"
              data-testid={`view-cashier-${c.id}`}
              aria-label="View cashier"
            >
              <Eye size={14} />
            </Button>
          </span>
        )}
      />

      {/* Pagination */}
      <DataPagination
        page={safePage}
        pageSize={pageSize}
        total={totalFiltered}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        onPageSizeChange={(n) => { setPageSize(n); setCurrentPage(1); }}
        testId="cashiers-pagination"
      />

      {/* Create Cashier Dialog — Multi-Step */}
      <Dialog open={isDialogOpen} onOpenChange={(open) => { if (!open) setStep(0); setIsDialogOpen(open); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Add Cashier</DialogTitle>
            <DialogDescription className="sr-only">Assign a user as cashier with operational settings</DialogDescription>
          </DialogHeader>

          {/* Stepper */}
          <div className="flex items-center justify-between mb-6 px-2">
            {formSteps.map((s, i) => {
              const Icon = s.icon;
              const isActive = i === step;
              const isDone = i < step;
              return (
                <div key={s.label} className="flex items-center flex-1 last:flex-none">
                  <button type="button" onClick={() => setStep(i)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                      isActive ? 'bg-[#0A2540] text-white' : isDone ? 'bg-[#10B981]/10 text-[#10B981]' : 'bg-[#FCFAF6] text-[#78716C]'
                    }`} data-testid={`cashier-step-${i}`}>
                    <Icon size={14} />
                    <span className="hidden sm:inline">{s.label}</span>
                  </button>
                  {i < formSteps.length - 1 && <div className={`flex-1 h-px mx-2 ${isDone ? 'bg-[#10B981]/30' : 'bg-[#EDE6D8]'}`} />}
                </div>
              );
            })}
          </div>

          <form onSubmit={handleSubmit}>
            {/* Step 0: Assignment */}
            {step === 0 && (
              <div className="space-y-4" data-testid="cashier-step-assignment">
                <p className="text-xs text-[#78716C]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Select user, agent, POS, and campus.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Full Name <span className="text-[#EF4444]">*</span></Label>
                    <Select value={formData.user_id} onValueChange={(v) => updateField('user_id', v)}>
                      <SelectTrigger className="border-[#EDE6D8] rounded-lg" data-testid="cashier-user-select">
                        <SelectValue placeholder="Select user" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableUsers.map(u => (
                          <SelectItem key={u.id} value={u.id}>{u.fullname}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Cashier Type <span className="text-[#EF4444]">*</span></Label>
                    <Select value={formData.cashier_type} onValueChange={(v) => updateField('cashier_type', v)}>
                      <SelectTrigger className="border-[#EDE6D8] rounded-lg" data-testid="cashier-type-select">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Agent">Agent</SelectItem>
                        <SelectItem value="Ambassador">Ambassador</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Campus <span className="text-[#EF4444]">*</span></Label>
                    <Select value={formData.campus_id} onValueChange={(v) => updateField('campus_id', v)}>
                      <SelectTrigger className="border-[#EDE6D8] rounded-lg" data-testid="cashier-campus-select">
                        <SelectValue placeholder="Select campus" />
                      </SelectTrigger>
                      <SelectContent>
                        {campuses.map(c => <SelectItem key={c.id} value={c.id}>{c.campus_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}

            {/* Step 1: Float & Cards */}
            {step === 1 && (
              <div className="space-y-4" data-testid="cashier-step-float">
                <p className="text-xs text-[#78716C]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Set float, cards, and recharge parameters.
                </p>
                <div className={`grid ${formData.cashier_type === 'Ambassador' ? 'grid-cols-1' : 'grid-cols-2'} gap-4`}>
                  <div className="space-y-2">
                    <Label>Assigned Float</Label>
                    <Input value={formData.assigned_float} onChange={(e) => updateField('assigned_float', e.target.value)}
                      placeholder="e.g., 500000" className="border-[#EDE6D8] rounded-lg" data-testid="cashier-float-input" />
                  </div>
                  {formData.cashier_type !== 'Ambassador' && (
                    <div className="space-y-2">
                      <Label>Assigned Cards</Label>
                      <Input value={formData.assigned_cards} onChange={(e) => updateField('assigned_cards', e.target.value)}
                        placeholder="e.g., 100" className="border-[#EDE6D8] rounded-lg" data-testid="cashier-cards-input" />
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Recharge <span className="text-[#EF4444]">*</span></Label>
                    <Select value={formData.recharge} onValueChange={(v) => updateField('recharge', v)}>
                      <SelectTrigger className="border-[#EDE6D8] rounded-lg" data-testid="cashier-recharge-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {YES_NO.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div />
                </div>
              </div>
            )}

            {/* Step 2: Settings */}
            {step === 2 && (
              <div className="space-y-4" data-testid="cashier-step-settings">
                <p className="text-xs text-[#78716C]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Additional operational settings.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Sell Bottle <span className="text-[#EF4444]">*</span></Label>
                    <Select value={formData.sell_bottle} onValueChange={(v) => updateField('sell_bottle', v)}>
                      <SelectTrigger className="border-[#EDE6D8] rounded-lg" data-testid="cashier-sell-bottle-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {YES_NO.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Offer <span className="text-[#EF4444]">*</span></Label>
                    <Select value={formData.offer} onValueChange={(v) => updateField('offer', v)}>
                      <SelectTrigger className="border-[#EDE6D8] rounded-lg" data-testid="cashier-offer-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {YES_NO.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Sub Free Plan <span className="text-[#EF4444]">*</span></Label>
                    <Select value={formData.sub_free_plan} onValueChange={(v) => updateField('sub_free_plan', v)}>
                      <SelectTrigger className="border-[#EDE6D8] rounded-lg" data-testid="cashier-sub-free-plan-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {YES_NO.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Send SMS <span className="text-[#EF4444]">*</span></Label>
                    <Select value={formData.send_sms} onValueChange={(v) => updateField('send_sms', v)}>
                      <SelectTrigger className="border-[#EDE6D8] rounded-lg" data-testid="cashier-send-sms-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {YES_NO.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Footer */}
            <DialogFooter className="pt-6 mt-6 border-t border-[#EDE6D8] flex justify-between">
              <div>
                {step > 0 && (
                  <Button type="button" variant="outline" onClick={() => setStep(step - 1)}
                    className="border-[#EDE6D8] rounded-lg" data-testid="cashier-step-back-btn">Back</Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="border-[#EDE6D8] rounded-lg">Cancel</Button>
                {step < formSteps.length - 1 ? (
                  <Button type="button" onClick={() => {
                    if (step === 0 && (!formData.user_id || !formData.campus_id)) {
                      toast.error('User and campus are required');
                      return;
                    }
                    setStep(step + 1);
                  }} className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg" data-testid="cashier-step-next-btn">Next</Button>
                ) : (
                  <Button type="submit" disabled={submitting}
                    className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg" data-testid="save-cashier-btn">
                    {submitting ? 'Saving...' : 'Create'}
                  </Button>
                )}
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Cashier Dialog */}
      <Dialog open={editOpen} onOpenChange={(open) => { setEditOpen(open); if (!open) { setEditing(null); setEditShowMore(false); setEditTab('float'); setAnalytics(null); setMobileSession(null); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-[#EDE6D8]">
            <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {editing ? `${editing.user_fullname || editing.user_name || ''}` : 'Cashier'}
            </DialogTitle>
            <DialogDescription>
              {editing ? `@${editing.user_name || ''} • ${editing.campus_name || '—'}` : 'Manage cashier details, float, and analytics.'}
            </DialogDescription>
          </DialogHeader>

          <Tabs value={editTab} onValueChange={setEditTab} className="flex flex-col flex-1 min-h-0">
            <div className="px-6 pt-4 bg-[#FCFAF6]/40 border-b border-[#EDE6D8]">
              <TabsList className="bg-transparent p-0 h-auto gap-1">
                <TabsTrigger
                  value="float"
                  className="rounded-xl px-4 py-2 text-xs font-semibold tracking-wide data-[state=active]:bg-[#0A2540] data-[state=active]:text-white data-[state=inactive]:text-[#78716C]"
                  data-testid="tab-float-management"
                >
                  <CurrencyDollar size={14} className="mr-1.5" weight="fill" />
                  Float Management
                </TabsTrigger>
                <TabsTrigger
                  value="details"
                  className="rounded-xl px-4 py-2 text-xs font-semibold tracking-wide data-[state=active]:bg-[#0A2540] data-[state=active]:text-white data-[state=inactive]:text-[#78716C]"
                  data-testid="tab-cashier-details"
                >
                  <Gear size={14} className="mr-1.5" weight="fill" />
                  Cashier Details
                </TabsTrigger>
                <TabsTrigger
                  value="analytics"
                  className="rounded-xl px-4 py-2 text-xs font-semibold tracking-wide data-[state=active]:bg-[#0A2540] data-[state=active]:text-white data-[state=inactive]:text-[#78716C]"
                  data-testid="tab-cashier-analytics"
                >
                  <ChartLineUp size={14} className="mr-1.5" weight="fill" />
                  Cashier Analytics
                </TabsTrigger>
                {isSuperAdmin && (
                  <TabsTrigger
                    value="mobile-session"
                    className="rounded-xl px-4 py-2 text-xs font-semibold tracking-wide data-[state=active]:bg-[#0A2540] data-[state=active]:text-white data-[state=inactive]:text-[#78716C]"
                    data-testid="tab-cashier-mobile-session"
                  >
                    <DeviceMobile size={14} className="mr-1.5" weight="fill" />
                    Mobile Session
                  </TabsTrigger>
                )}
              </TabsList>
            </div>

            {editLoading ? (
              <div className="flex items-center justify-center py-16" data-testid="cashier-detail-loading">
                <Spinner size={32} className="animate-spin text-[#0A2540]" />
              </div>
            ) : (
            <form onSubmit={handleEditSubmit} className="flex flex-col flex-1 min-h-0">
              <div className="flex-1 overflow-y-auto px-6 py-5">

                {/* ─── FLOAT MANAGEMENT TAB ─── */}
                <TabsContent value="float" className="mt-0 space-y-5 data-[state=inactive]:hidden" forceMount>
                  {editing && (
                    <div className="rounded-2xl border border-[#0A2540]/15 bg-[#0A2540]/5 p-5 grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-[10px] tracking-[0.15em] uppercase text-[#0A2540] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                          Current Float Balance
                        </p>
                        <p className="text-3xl font-bold font-mono text-[#1C1917] mt-1.5">
                          {Number(editing.assigned_float || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                        {editForm.float_delta && Number(editForm.float_delta) !== 0 && (
                          <p className="text-xs text-[#78716C] mt-1.5 font-mono">
                            → {(Number(editing.assigned_float || 0) + Number(editForm.float_delta)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} after change
                          </p>
                        )}
                      </div>
                      <div>
                        <p className="text-[10px] tracking-[0.15em] uppercase text-[#0A2540] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                          Current Card Balance
                        </p>
                        <p className="text-3xl font-bold font-mono text-[#1C1917] mt-1.5">
                          {Number(editing.assigned_cards || 0).toLocaleString()}
                        </p>
                        {editForm.card_delta && Number(editForm.card_delta) !== 0 && (
                          <p className="text-xs text-[#78716C] mt-1.5 font-mono">
                            → {(Number(editing.assigned_cards || 0) + Number(editForm.card_delta)).toLocaleString()} after change
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="rounded-2xl bg-[#FCFAF6] border border-[#EDE6D8] p-5 space-y-4">
                    <div className="flex items-center gap-2">
                      <CurrencyDollar size={16} className="text-[#0A2540]" weight="fill" />
                      <p className="text-xs font-semibold uppercase tracking-wider text-[#1C1917]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                        Top-up / Adjust
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs">Float Delta <span className="text-[#78716C] font-normal">(negative = deduct)</span></Label>
                        <div className="relative">
                          <Input
                            type="number"
                            step="0.01"
                            value={editForm.float_delta}
                            onChange={(e) => setEditForm({ ...editForm, float_delta: e.target.value })}
                            placeholder="0"
                            className="border-[#EDE6D8] rounded-lg pl-9 font-mono"
                            data-testid="edit-cashier-float-delta"
                          />
                          {editForm.float_delta && Number(editForm.float_delta) !== 0 && (
                            <div className="absolute left-2 top-1/2 -translate-y-1/2">
                              {Number(editForm.float_delta) > 0
                                ? <ArrowUp size={14} className="text-emerald-600" weight="bold" />
                                : <ArrowDown size={14} className="text-amber-600" weight="bold" />}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Card Delta <span className="text-[#78716C] font-normal">(integer; negative = deduct)</span></Label>
                        <div className="relative">
                          <Input
                            type="number"
                            step="1"
                            value={editForm.card_delta}
                            onChange={(e) => setEditForm({ ...editForm, card_delta: e.target.value })}
                            placeholder="0"
                            className="border-[#EDE6D8] rounded-lg pl-9 font-mono"
                            data-testid="edit-cashier-card-delta"
                          />
                          {editForm.card_delta && Number(editForm.card_delta) !== 0 && (
                            <div className="absolute left-2 top-1/2 -translate-y-1/2">
                              {Number(editForm.card_delta) > 0
                                ? <ArrowUp size={14} className="text-emerald-600" weight="bold" />
                                : <ArrowDown size={14} className="text-amber-600" weight="bold" />}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <p className="text-[11px] text-[#78716C]">Leave blank or 0 to keep current values unchanged. Save Changes applies the delta on the middleware.</p>
                  </div>
                </TabsContent>

                {/* ─── CASHIER DETAILS TAB ─── */}
                <TabsContent value="details" className="mt-0 space-y-5 data-[state=inactive]:hidden" forceMount>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs">Cashier Type</Label>
                      <Select value={editForm.cashier_type} onValueChange={(v) => setEditForm({ ...editForm, cashier_type: v })}>
                        <SelectTrigger className="border-[#EDE6D8] rounded-lg" data-testid="edit-cashier-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Agent">Agent</SelectItem>
                          <SelectItem value="Ambassador">Ambassador</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Campus</Label>
                      <Select value={editForm.campus_id} onValueChange={(v) => setEditForm({ ...editForm, campus_id: v })}>
                        <SelectTrigger className="border-[#EDE6D8] rounded-lg" data-testid="edit-cashier-campus">
                          <SelectValue placeholder="Select campus" />
                        </SelectTrigger>
                        <SelectContent>
                          {campuses.map(c => (
                            <SelectItem key={c.id} value={c.id}>{c.campus_name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Recharge Limit</Label>
                      <Input
                        type="number"
                        value={editForm.recharge_limit}
                        onChange={(e) => setEditForm({ ...editForm, recharge_limit: e.target.value })}
                        placeholder="50000"
                        className="border-[#EDE6D8] rounded-lg font-mono"
                        data-testid="edit-cashier-recharge-limit"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Status</Label>
                      <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                        <SelectTrigger className="border-[#EDE6D8] rounded-lg" data-testid="edit-cashier-status">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Active">Active</SelectItem>
                          <SelectItem value="Inactive">Inactive</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wider text-[#1C1917] pt-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      Permissions
                    </p>
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        { key: 'recharge', label: 'Recharge', tid: 'edit-recharge' },
                        { key: 'sell_bottle', label: 'Sell Bottle', tid: 'edit-sell-bottle' },
                        { key: 'special_offer', label: 'Special Offer', tid: 'edit-special-offer' },
                        { key: 'sub_free_plan', label: 'Sub Free Plan', tid: 'edit-sub-free-plan' },
                        { key: 'send_sms', label: 'Send SMS', tid: 'edit-send-sms' },
                      ].map(f => (
                        <div key={f.key} className="space-y-2">
                          <Label className="text-xs">{f.label}</Label>
                          <Select
                            value={editForm[f.key]}
                            onValueChange={(v) => setEditForm({ ...editForm, [f.key]: v })}
                          >
                            <SelectTrigger className="border-[#EDE6D8] rounded-lg" data-testid={f.tid}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Y">Yes</SelectItem>
                              <SelectItem value="N">No</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      ))}
                    </div>
                  </div>
                </TabsContent>

                {/* ─── CASHIER ANALYTICS TAB ─── */}
                <TabsContent value="analytics" className="mt-0 space-y-5 data-[state=inactive]:hidden" forceMount>
                  {analyticsLoading ? (
                    <div className="py-16 text-center text-sm text-[#78716C] font-mono">
                      <Spinner size={20} className="animate-spin inline mr-2" />
                      Loading analytics…
                    </div>
                  ) : !analytics ? (
                    <div className="py-16 text-center text-sm text-[#78716C]">No analytics available.</div>
                  ) : (
                    <>
                      {/* Today stat cards */}
                      <div>
                        <p className="text-[10px] tracking-[0.15em] uppercase text-[#0A2540] font-semibold mb-3" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                          Today
                        </p>
                        <div className="grid grid-cols-3 gap-3">
                          <div className="rounded-2xl border border-[#EDE6D8] bg-white p-4">
                            <p className="text-[10px] uppercase tracking-wider text-[#78716C] font-mono">Registrations</p>
                            <p className="text-2xl font-bold font-mono text-[#1C1917] mt-1.5" data-testid="analytics-today-regs">
                              {analytics.today.registrations}
                            </p>
                          </div>
                          <div className="rounded-2xl border border-[#EDE6D8] bg-white p-4">
                            <p className="text-[10px] uppercase tracking-wider text-[#78716C] font-mono">Recharges</p>
                            <p className="text-2xl font-bold font-mono text-[#1C1917] mt-1.5" data-testid="analytics-today-recharges">
                              {analytics.today.recharges}
                            </p>
                          </div>
                          <div className="rounded-2xl border border-[#EDE6D8] bg-white p-4">
                            <p className="text-[10px] uppercase tracking-wider text-[#78716C] font-mono">Amount</p>
                            <p className="text-2xl font-bold font-mono text-[#1C1917] mt-1.5" data-testid="analytics-today-amount">
                              {Number(analytics.today.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Lifetime stats */}
                      <div>
                        <p className="text-[10px] tracking-[0.15em] uppercase text-[#0A2540] font-semibold mb-3" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                          Lifetime
                        </p>
                        <div className="grid grid-cols-4 gap-3">
                          <div className="rounded-2xl bg-[#0A2540] p-4 text-white">
                            <p className="text-[10px] uppercase tracking-wider opacity-70 font-mono">Subscribers</p>
                            <p className="text-2xl font-bold font-mono mt-1.5" data-testid="analytics-life-regs">
                              {analytics.lifetime.registrations}
                            </p>
                          </div>
                          <div className="rounded-2xl bg-[#0A2540] p-4 text-white">
                            <p className="text-[10px] uppercase tracking-wider opacity-70 font-mono">Recharges</p>
                            <p className="text-2xl font-bold font-mono mt-1.5" data-testid="analytics-life-recharges">
                              {analytics.lifetime.recharges}
                            </p>
                          </div>
                          <div className="rounded-2xl bg-[#0A2540] p-4 text-white">
                            <p className="text-[10px] uppercase tracking-wider opacity-70 font-mono">Txns</p>
                            <p className="text-2xl font-bold font-mono mt-1.5" data-testid="analytics-life-txns">
                              {analytics.lifetime.total_transactions}
                            </p>
                          </div>
                          <div className="rounded-2xl bg-[#0A2540] p-4 text-white">
                            <p className="text-[10px] uppercase tracking-wider opacity-70 font-mono">Volume</p>
                            <p className="text-xl font-bold font-mono mt-1.5" data-testid="analytics-life-volume">
                              {Number(analytics.lifetime.total_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Recent transactions table */}
                      <div className="rounded-2xl border border-[#EDE6D8] bg-white overflow-hidden">
                        <div className="px-5 py-3 border-b border-[#EDE6D8] bg-[#FCFAF6]/40">
                          <p className="text-xs font-semibold uppercase tracking-wider text-[#1C1917]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                            Recent Transactions ({analytics.recent_transactions.length})
                          </p>
                        </div>
                        {analytics.recent_transactions.length === 0 ? (
                          <div className="px-5 py-8 text-center text-xs text-[#78716C]">No transactions on record.</div>
                        ) : (
                          <div className="max-h-64 overflow-y-auto">
                            {analytics.recent_transactions.map((t, i) => (
                              <div key={t.id || i} className="px-5 py-2.5 flex items-center justify-between border-b border-[#EDE6D8] last:border-b-0 hover:bg-[#FCFAF6]/40">
                                <div className="min-w-0">
                                  <p className="text-xs font-semibold text-[#1C1917] truncate" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                                    {t.narration || '—'} <span className="text-[#78716C] font-normal">· {t.campus_name || '—'}</span>
                                  </p>
                                  <p className="text-[10px] text-[#78716C] font-mono truncate">
                                    {t.trn_date} • {t.card_no || '—'} • Ref: {t.trn_ref_no || '—'}
                                  </p>
                                </div>
                                <p className="text-xs font-bold font-mono text-[#0A2540] shrink-0 ml-3">
                                  {Number(t.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {t.currency}
                                </p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </TabsContent>

                {/* ─── MOBILE SESSION TAB (super-admin only) ─── */}
                {isSuperAdmin && (
                  <TabsContent value="mobile-session" className="mt-0 space-y-5 data-[state=inactive]:hidden" forceMount>
                    {mobileSessionLoading ? (
                      <div className="flex items-center justify-center py-16" data-testid="mobile-session-loading">
                        <Spinner size={32} className="animate-spin text-[#0A2540]" />
                      </div>
                    ) : !mobileSession ? (
                      <div className="rounded-2xl border border-[#EDE6D8] bg-[#FCFAF6] p-8 text-center">
                        <DeviceMobile size={32} className="mx-auto text-[#A8A29E] mb-2" weight="duotone" />
                        <p className="text-sm text-[#78716C]">Open the Mobile Session tab to load.</p>
                      </div>
                    ) : !mobileSession.active ? (
                      <div className="rounded-2xl border border-[#EDE6D8] bg-[#FCFAF6] p-8 text-center" data-testid="mobile-session-inactive">
                        <DeviceMobile size={32} className="mx-auto text-[#A8A29E] mb-2" weight="duotone" />
                        <p className="text-sm font-semibold text-[#1C1917]">No active mobile session</p>
                        <p className="text-xs text-[#78716C] mt-1">
                          {mobileSession.user?.last_mobile_login
                            ? `Last mobile login ${timeAgo(mobileSession.user.last_mobile_login)}.`
                            : 'This cashier has never signed in on the mobile app.'}
                        </p>
                      </div>
                    ) : (
                      <>
                        <div className="rounded-2xl border border-[#0A2540]/15 bg-[#0A2540]/5 p-5" data-testid="mobile-session-card">
                          <div className="flex items-start justify-between gap-4">
                            <div className="min-w-0">
                              <p className="text-[10px] tracking-[0.15em] uppercase text-[#0A2540] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                                Active Device
                              </p>
                              <p className="text-lg font-bold text-[#1C1917] mt-1 truncate">
                                {mobileSession.session.device_name || 'Unknown device'}
                              </p>
                              <p className="text-[11px] text-[#78716C] font-mono mt-0.5 truncate">
                                {mobileSession.session.device_id || '—'}
                              </p>
                            </div>
                            <div className="shrink-0">
                              {mobileSession.session.pin_enabled ? (
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-semibold tracking-wide" data-testid="pin-enabled-badge">
                                  <ShieldCheck size={12} weight="fill" />
                                  PIN ENABLED
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#FCFAF6] text-[#78716C] text-[10px] font-semibold tracking-wide border border-[#EDE6D8]" data-testid="pin-disabled-badge">
                                  PIN OFF
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-4 mt-5 pt-5 border-t border-[#0A2540]/10">
                            <div>
                              <p className="text-[10px] tracking-[0.15em] uppercase text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                                Signed In
                              </p>
                              <p className="text-sm font-semibold text-[#1C1917] mt-1">
                                {timeAgo(mobileSession.session.created_at)}
                              </p>
                              <p className="text-[10px] text-[#A8A29E] font-mono">
                                {mobileSession.session.created_at?.slice(0, 19).replace('T', ' ')}
                              </p>
                            </div>
                            <div>
                              <p className="text-[10px] tracking-[0.15em] uppercase text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                                Refresh Expires
                              </p>
                              <p className="text-sm font-semibold text-[#1C1917] mt-1">
                                {mobileSession.session.expires_at?.slice(0, 10) || '—'}
                              </p>
                              <p className="text-[10px] text-[#A8A29E] font-mono">
                                {mobileSession.session.rotated ? 'rotated' : 'fresh'}
                              </p>
                            </div>
                          </div>

                          {mobileSession.session.pin_enabled && mobileSession.session.pin_enabled_at && (
                            <p className="text-[11px] text-emerald-700 font-mono mt-4 pt-4 border-t border-[#0A2540]/10">
                              PIN enabled {timeAgo(mobileSession.session.pin_enabled_at)} — the cashier can sign in with a 4-digit PIN.
                            </p>
                          )}
                        </div>

                        <div className="rounded-2xl border border-rose-200 bg-rose-50/50 p-5" data-testid="revoke-section">
                          <div className="flex items-start gap-3">
                            <Prohibit size={18} className="text-rose-600 shrink-0 mt-0.5" weight="fill" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-rose-900">Force sign-out</p>
                              <p className="text-xs text-rose-800/80 mt-1 leading-relaxed">
                                Revokes the refresh token. The cashier&apos;s phone is signed out within seconds. They must log in again with phone + password — their PIN alone will not work.
                              </p>
                              <Button
                                type="button"
                                onClick={handleRevokeMobileSession}
                                disabled={revokingSession}
                                className="mt-3 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-xs h-8 px-3"
                                data-testid="revoke-mobile-session-btn"
                              >
                                {revokingSession ? <Spinner size={12} className="animate-spin mr-1.5" /> : <Prohibit size={12} className="mr-1.5" weight="fill" />}
                                {revokingSession ? 'Revoking…' : 'Revoke mobile session'}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </TabsContent>
                )}

              </div>

              <DialogFooter className="px-6 py-4 border-t border-[#EDE6D8] bg-[#FFFFFF]">
                <Button type="button" variant="outline" className="border-[#EDE6D8] rounded-lg"
                  onClick={() => { setEditOpen(false); setEditing(null); setEditShowMore(false); setEditTab('float'); setAnalytics(null); setMobileSession(null); }}>
                  Cancel
                </Button>
                <Button type="submit" disabled={editSubmitting || editTab === 'analytics' || editTab === 'mobile-session'}
                  className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg" data-testid="save-edit-cashier-btn">
                  {editSubmitting ? <Spinner size={14} className="animate-spin mr-2" /> : null}
                  {editSubmitting ? 'Saving...' : 'Save Changes'}
                </Button>
              </DialogFooter>
            </form>
            )}
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
}
